import React, { useEffect, useState, useCallback } from 'react'
import {
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@material-ui/core'
import MoreOptions from '../common/MoreOptions'
import styleClasses from './table.module.scss'
import TableFilters from './TableFilters'
import CustomPagination from './CustomPagination'
import CircularProgress from '@material-ui/core/CircularProgress'
import { debounce } from 'lodash'

const CustomTable = ({
  columns,
  data,
  options,
  searchTitle = false,
  searchPlaceholder = false,
  buildingFilter,
  building,
  apartmentFilter,
  getData,
  customFilters,
  rightChildren,
  loading,
  serverPagination,
  serverCount,
  handleRowClick,
  getRowStyle,
  handleFiltersChange,
  subRowField,
  onPageChange,
  onParamsChange,
}) => {
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [filtersParams, setFiltersParams] = useState({})

  const getDataFromServer = (params) => {
    const getParams = {
      ...filtersParams,
      ...params,
      limit: rowsPerPage,
      offset: params.offset ?? 0,
    }
    if (building) {
      getParams.buildingId = building.id
    }
    getData(getParams)
  }

  useEffect(() => {
    if (onParamsChange) {
      onParamsChange && onParamsChange(filtersParams)
    }
  }, [filtersParams, onParamsChange])

  const handleChangePage = (newPage) => {
    if (serverPagination) {
      const newParams = {
        offset: newPage * rowsPerPage,
      }
      getDataFromServer(newParams)
      setPage(newPage)
    } else {
      setPage(newPage)
    }
    onPageChange && onPageChange(filtersParams, newPage)
  }

  // Deleting the only row of the last page leaves the table past the end, with no
  // rows and so no pagination controls to get back with.
  useEffect(() => {
    if (!serverPagination || loading || page === 0 || !serverCount) return
    if (page * rowsPerPage >= serverCount) {
      handleChangePage(Math.ceil(serverCount / rowsPerPage) - 1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverPagination, loading, page, rowsPerPage, serverCount])

  const tableData = serverPagination
    ? data
    : data?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  const debouncedGetData = useCallback(
    debounce((params) => {
      getData(params)
    }, 1000),
    [getData]
  )

  useEffect(() => {
    const params = {
      ...filtersParams,
      limit: rowsPerPage,
      offset: page * rowsPerPage,
    }
    if (building) {
      params.buildingId = building.id
    }
    debouncedGetData(params)

    return () => {
      debouncedGetData.cancel()
    }
  }, [filtersParams, building])

  return (
    <>
      <TableFilters
        searchTitle={searchTitle}
        searchPlaceholder={searchPlaceholder}
        apartmentFilter={apartmentFilter}
        buildingFilter={buildingFilter}
        building={building}
        handleParamsChange={(params) => {
          const newParams = { ...params }
          if (params?.buildingId?.id)
            newParams.buildingId = params?.buildingId?.id
          if (params?.apartmentId?.id)
            newParams.apartmentId = params?.apartmentId
          if (params?.search) newParams.search = params?.search
          newParams.offset = 0
          setFiltersParams(newParams)
          setPage(0)
          handleFiltersChange && handleFiltersChange(newParams)
        }}
        customFilters={customFilters}
        rightChildren={rightChildren}
        serverPagination={serverPagination}
        serverCount={serverCount}
      />
      {loading && (
        <div
          style={{
            minHeight: '660px',
            verticalAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress style={{ height: '100%' }} />
        </div>
      )}
      {!loading && (
        <div className={styleClasses.tableContainer}>
          <Table
            stickyHeader
            aria-label="sticky table"
            className={styleClasses.table}
          >
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    style={{ minWidth: column.minWidth }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {tableData?.map((row) => (
                <>
                  <TableRow
                    key={row.id}
                    onClick={(evt) => {
                      handleRowClick && handleRowClick(row)
                      evt.stopPropagation()
                    }}
                    style={{ cursor: handleRowClick ? 'pointer' : 'default', ...(getRowStyle ? getRowStyle(row) : {}) }}
                  >
                    {columns.map((column) => {
                      if (column.format) {
                        return (
                          <TableCell key={column.id} align={column.align}>
                            {column.format(row)}
                          </TableCell>
                        )
                      }
                      if (!column.id && column.minWidth && options) {
                        return (
                          <TableCell
                            style={{ width: 48 }}
                            key={column.id}
                            align={column.align}
                          >
                            <MoreOptions options={options(row)} />
                          </TableCell>
                        )
                      }
                      return (
                        <TableCell key={column.id} align={column.align}>
                          {typeof row[column.id] === 'boolean'
                            ? row[column.id]
                              ? 'Sí'
                              : 'No'
                            : row[column.id] !== null &&
                              row[column.id] !== undefined
                            ? row[column.id]
                            : ' - '}
                        </TableCell>
                      )
                    })}
                  </TableRow>
                  {row[subRowField] &&
                    row[subRowField].map((subRow) => (
                      <TableRow>
                        {columns.map((column) => {
                          return (
                            <TableCell key={column.id} align={column.align}>
                              {column.subRow
                                ? column.subRow(subRow) || ' - '
                                : ''}
                            </TableCell>
                          )
                        })}
                      </TableRow>
                    ))}
                </>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {!loading && !!data?.length && (
        <CustomPagination
          count={serverPagination ? serverCount : data?.length}
          page={page}
          onChangePage={handleChangePage}
          rowsPerPage={rowsPerPage}
        />
      )}
    </>
  )
}

export default CustomTable
